import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, CheckCircle, Clock, Bell, RefreshCw, Play, ChevronRight, Scan, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const PickerDashboard = () => {
  const navigate = useNavigate();
  const scanInputRef = useRef(null);
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Active slip state
  const [activeSlip, setActiveSlip] = useState(null);
  const [slipDetails, setSlipDetails] = useState(null);
  const [bundles, setBundles] = useState([]);
  
  // Scan state
  const [scanMode, setScanMode] = useState(false);
  const [currentBundle, setCurrentBundle] = useState(null);
  const [bundleScan, setBundleScan] = useState('');
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  
  // Success confirmation dialog for completed slip
  const [completionDialogOpen, setCompletionDialogOpen] = useState(false);
  const [completedSlipInfo, setCompletedSlipInfo] = useState(null);

  // Load data on mount
  useEffect(() => {
    fetchProfile();
    fetchMySlips();
    fetchNotifications();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await apiClient.get('/wms/picker/my-profile');
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const fetchMySlips = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/wms/picker/my-slips');
      const slips = response.data.slips || [];
      
      // Get the active slip (first ASSIGNED or IN_PROGRESS)
      const active = slips.find(s => ['ASSIGNED', 'IN_PROGRESS'].includes(s.status));
      setActiveSlip(active);
      
      if (active) {
        fetchSlipDetails(active.slip_id);
      }
    } catch (error) {
      toast.error('Failed to load slips');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSlipDetails = async (slipId) => {
    try {
      const response = await apiClient.get(`/wms/picker/slip-details/${slipId}`);
      setSlipDetails(response.data);
      setBundles(response.data.bundles || []);
    } catch (error) {
      console.error('Failed to load slip details:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await apiClient.get('/wms/picker/notifications?unread_only=true');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
      
      // Show toast for new notifications
      if (response.data.unread_count > 0) {
        const latest = response.data.notifications[0];
        if (latest && !latest.read) {
          toast.info(latest.message, { duration: 5000 });
        }
      }
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const handleStartPicking = async () => {
    if (!activeSlip) return;
    
    try {
      await apiClient.post('/wms/picker/start-picking', {
        slip_id: activeSlip.slip_id
      });
      toast.success('Picking started');
      fetchMySlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start picking');
    }
  };

  const handleSelectBundle = (bundle) => {
    if (bundle.picked) return;
    setCurrentBundle(bundle);
    setScanMode(true);
    setBundleScan('');
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  const handleConfirmPick = async () => {
    if (!activeSlip || !currentBundle || !bundleScan) {
      toast.error('Please scan the bundle barcode');
      return;
    }
    
    // Validate bundle code matches
    if (bundleScan !== currentBundle.bundle_code) {
      toast.error(`Bundle mismatch! Expected: ${currentBundle.bundle_code}`);
      return;
    }
    
    try {
      await apiClient.post('/wms/picker/confirm-pick', {
        slip_id: activeSlip.slip_id,
        bundle_code: bundleScan,
        bin_location: currentBundle.bin_location
      });
      
      toast.success(`Bundle ${currentBundle.bundle_code} picked!`);
      
      // Update local state
      setBundles(prev => prev.map(b => 
        b.bundle_code === currentBundle.bundle_code 
          ? { ...b, picked: true }
          : b
      ));
      
      setScanMode(false);
      setCurrentBundle(null);
      setBundleScan('');
      
      // Refresh details
      fetchSlipDetails(activeSlip.slip_id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm pick');
    }
  };

  const handleCompleteSlip = async () => {
    if (!activeSlip) return;
    
    const remaining = bundles.filter(b => !b.picked).length;
    if (remaining > 0) {
      toast.error(`Cannot complete. ${remaining} bundles remaining.`);
      return;
    }
    
    try {
      const response = await apiClient.post('/wms/picker/complete-slip', {
        slip_id: activeSlip.slip_id
      });
      
      // Store completed slip info for success dialog
      setCompletedSlipInfo({
        slipNumber: activeSlip.slip_no || activeSlip.pick_slip_number,
        totalBundles: bundles.length,
        nextSlip: response.data.next_slip
      });
      
      // Show success confirmation dialog
      setCompletionDialogOpen(true);
      
      // Vibrate for success feedback on mobile
      vibrate([200, 100, 200]);
      
      // Refresh data in background
      fetchMySlips();
      fetchProfile();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete slip');
    }
  };
  
  const handleCloseCompletionDialog = () => {
    setCompletionDialogOpen(false);
    
    // If there's a next slip, show a toast about it
    if (completedSlipInfo?.nextSlip) {
      toast.info(`Next slip assigned: ${completedSlipInfo.nextSlip.slip_no}`, { duration: 5000 });
    }
    
    setCompletedSlipInfo(null);
  };

  const handleMarkNotificationRead = async (notificationId) => {
    try {
      await apiClient.post(`/wms/picker/notifications/mark-read/${notificationId}`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const vibrate = (pattern) => {
    if (navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/dashboard')}
            className="text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-bold">Picker Dashboard</h1>
            <p className="text-xs text-gray-400">{profile?.staff_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowNotifications(!showNotifications)}
            className="relative"
          >
            <Bell className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={fetchMySlips}>
            <RefreshCw className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="bg-gray-800 border-b border-gray-700 p-4 max-h-64 overflow-y-auto">
          <h3 className="font-bold mb-2">Notifications</h3>
          {notifications.length === 0 ? (
            <p className="text-gray-400 text-sm">No new notifications</p>
          ) : (
            notifications.map(notif => (
              <div
                key={notif.notification_id}
                className="p-2 bg-gray-700 rounded mb-2 cursor-pointer"
                onClick={() => handleMarkNotificationRead(notif.notification_id)}
              >
                <p className="text-sm">{notif.message}</p>
                <p className="text-xs text-gray-400">{new Date(notif.created_at).toLocaleTimeString()}</p>
              </div>
            ))
          )}
        </div>
      )}

      <main className="p-4 space-y-4">
        {/* No Active Slip */}
        {!activeSlip && (
          <div className="text-center py-16">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-600" />
            <h2 className="text-xl font-bold mb-2">No Active Pick Slip</h2>
            <p className="text-gray-400">Waiting for assignment...</p>
            <Button className="mt-4" onClick={fetchMySlips}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Check for Slips
            </Button>
          </div>
        )}

        {/* Active Slip */}
        {activeSlip && !scanMode && (
          <>
            {/* Slip Info Card */}
            <Card className="bg-gray-800 border-gray-700 text-white">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg text-white">{activeSlip.slip_no}</CardTitle>
                  <Badge variant={activeSlip.status === 'IN_PROGRESS' ? 'default' : 'secondary'}>
                    {activeSlip.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">SKU</p>
                    <p className="font-mono text-white">{activeSlip.sku_code}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Quantity</p>
                    <p className="font-bold text-lg text-white">{activeSlip.pick_qty} pcs</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400">Design</p>
                    <p className="text-white">{slipDetails?.item?.design_name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Shade</p>
                    <p className="text-white" data-testid="picker-shade">{slipDetails?.item?.shade || '-'}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Size</p>
                    <p className="text-white" data-testid="picker-size">{slipDetails?.item?.size || '-'}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-gray-400">Description</p>
                    <p className="text-white text-xs" data-testid="picker-description">{slipDetails?.item?.description || '-'}</p>
                  </div>
                </div>
                
                {activeSlip.status === 'ASSIGNED' && (
                  <Button 
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    onClick={handleStartPicking}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Picking
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Progress */}
            {activeSlip.status === 'IN_PROGRESS' && slipDetails && (
              <Card className="bg-gray-800 border-gray-700 text-white">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Progress</span>
                    <span className="font-bold text-white">
                      {slipDetails.picked_bundles}/{slipDetails.total_bundles} bundles
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-3">
                    <div 
                      className="bg-green-500 h-3 rounded-full transition-all"
                      style={{ width: `${(slipDetails.picked_bundles / slipDetails.total_bundles) * 100}%` }}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Bundle List */}
            {activeSlip.status === 'IN_PROGRESS' && (
              <div className="space-y-2">
                <h3 className="font-bold text-white">Pick List</h3>
                {bundles.map((bundle, idx) => (
                  <div
                    key={bundle.bundle_code}
                    onClick={() => handleSelectBundle(bundle)}
                    className={`p-4 rounded-lg flex items-center justify-between cursor-pointer transition-all ${
                      bundle.picked 
                        ? 'bg-green-900/30 border border-green-700' 
                        : 'bg-gray-800 border border-gray-700 active:bg-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
                        bundle.picked ? 'bg-green-600' : 'bg-gray-700'
                      }`}>
                        {bundle.picked ? <CheckCircle className="w-5 h-5" /> : idx + 1}
                      </div>
                      <div>
                        <p className="font-mono text-sm text-white">{bundle.bundle_code}</p>
                        <p className="text-xs text-gray-400">{bundle.usn_count} pcs</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-blue-400">
                        <MapPin className="w-4 h-4" />
                        <span className="font-mono">{bundle.bin_location}</span>
                      </div>
                      {!bundle.picked && <ChevronRight className="w-5 h-5 text-gray-500 mt-1" />}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Complete Button */}
            {activeSlip.status === 'IN_PROGRESS' && slipDetails?.remaining_bundles === 0 && (
              <Button 
                className="w-full h-14 text-lg bg-green-600 hover:bg-green-700"
                onClick={handleCompleteSlip}
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Complete Pick Slip
              </Button>
            )}
          </>
        )}

        {/* Scan Mode */}
        {scanMode && currentBundle && (
          <div className="space-y-4">
            <Card className="bg-blue-900/30 border-blue-700 text-white">
              <CardContent className="pt-4">
                <div className="text-center mb-4">
                  <Scan className="w-12 h-12 mx-auto mb-2 text-blue-400" />
                  <h2 className="text-xl font-bold text-white">Scan Bundle Barcode</h2>
                </div>
                
                <div className="bg-gray-800 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Go to Bin</span>
                    <span className="font-mono text-lg text-blue-400">{currentBundle.bin_location}</span>
                  </div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">Pick Bundle</span>
                    <span className="font-mono text-white">{currentBundle.bundle_code}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Quantity</span>
                    <span className="text-white">{currentBundle.usn_count} pcs</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <p className="text-sm text-gray-400 text-center">Scan bundle barcode to confirm pick</p>
                  <Input
                    ref={scanInputRef}
                    value={bundleScan}
                    onChange={(e) => setBundleScan(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleConfirmPick()}
                    placeholder="Scan bundle barcode..."
                    className="h-14 text-lg text-center bg-gray-700 border-gray-600 text-white"
                    autoFocus
                  />
                </div>
                
                <div className="flex gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1 border-gray-600 text-white hover:bg-gray-700"
                    onClick={() => {
                      setScanMode(false);
                      setCurrentBundle(null);
                      setBundleScan('');
                    }}
                  >
                    Cancel
                  </Button>
                  <Button 
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    onClick={handleConfirmPick}
                    disabled={!bundleScan}
                  >
                    Confirm Pick
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stats Summary */}
        {profile && (
          <Card className="bg-gray-800 border-gray-700 text-white mt-4">
            <CardContent className="pt-4">
              <h3 className="font-bold mb-3 text-white">Today's Stats</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center p-3 bg-gray-700 rounded">
                  <p className="text-2xl font-bold text-green-400">
                    {profile.sfg?.stats?.slips_completed || 0}
                  </p>
                  <p className="text-xs text-gray-400">Slips Completed</p>
                </div>
                <div className="text-center p-3 bg-gray-700 rounded">
                  <p className="text-2xl font-bold text-blue-400">
                    {profile.sfg?.stats?.total_picks || 0}
                  </p>
                  <p className="text-xs text-gray-400">Bundles Picked</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Success Confirmation Dialog for Completed Slip */}
      <Dialog open={completionDialogOpen} onOpenChange={setCompletionDialogOpen}>
        <DialogContent className="max-w-md bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <CheckCircle className="w-6 h-6" />
              Picking Completed Successfully!
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-6 text-center">
              <p className="text-sm text-gray-300 mb-2">Pick Slip Number:</p>
              <p className="text-3xl font-bold font-mono text-white mb-4" data-testid="completed-slip-number">
                {completedSlipInfo?.slipNumber}
              </p>
              <div className="border-t border-green-600/30 pt-4 mt-4">
                <p className="text-sm text-gray-300">
                  <span className="font-medium text-green-400">{completedSlipInfo?.totalBundles || 0}</span> bundle(s) picked successfully
                </p>
              </div>
            </div>
            
            {completedSlipInfo?.nextSlip && (
              <div className="bg-blue-900/30 border border-blue-600/50 rounded-lg p-4 mt-4 text-center">
                <p className="text-sm text-gray-300 mb-1">Next Slip Assigned:</p>
                <p className="text-lg font-bold text-blue-400">
                  {completedSlipInfo.nextSlip.slip_no}
                </p>
              </div>
            )}
            
            <p className="text-xs text-gray-400 text-center mt-4">
              You may take a screenshot or print this page for your records.
            </p>
          </div>
          <DialogFooter className="justify-center">
            <Button
              onClick={handleCloseCompletionDialog}
              className="bg-green-600 hover:bg-green-700 text-white px-8"
              data-testid="close-completion-dialog"
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PickerDashboard;
